import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Library, Plus, Search, Upload, X } from 'lucide-react';
import { ImportQuestionsModal } from 'components/questionBank/ImportQuestionsModal';
import { StatusBadge } from 'components/ui/status-badge';
import { LoadMore } from 'components/ui/pagination';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { useSubjectList } from 'modules/subject';
import { useBankQuestionList } from 'modules/questionBank';
import { QuestionSummary } from 'modules/questionBank/components/QuestionSummary';
import { useSearchParamState } from 'hooks/use-search-param-state';
import type { Subject } from 'modules/subject/types';
import type { BankQuestion } from 'modules/questionBank/types';
import {
  DEFAULT_TYPE_META,
  DIFFICULTIES,
  DIFFICULTY_TONE,
  QUESTION_TYPE_META,
  QUESTION_TYPE_ORDER,
  type Difficulty,
} from 'pages/QuestionBank/questionTypeMeta';
import { formatGroupedNumber } from 'lib/dateFormat';
import { QuestionPreviewPanel } from 'pages/QuestionBank/QuestionPreviewPanel';
import { useMobileDockOverlay } from 'components/mobile/MobileShell';
import {
  MobileScreen,
  ScreenHeader,
  HeaderIcon,
  HeaderPlus,
  ScreenBody,
  SearchInput,
  BottomSheet,
  MobileSkeleton,
  MobileEmpty,
  MobileError,
  M,
} from 'components/mobile';

// Show a search field in the subject sheet once an org carries enough
// subjects that scanning becomes work.
const SUBJECT_SEARCH_THRESHOLD = 6;

// Mirrors the desktop page's DEFAULT_PAGE_SIZE (pages/QuestionBank/QuestionBankPage.tsx)
// so mobile and desktop paginate identically.
const PAGE_SIZE = 20;

const DIFFICULTY_DOT: Record<Difficulty, string> = { easy: M.greenDot, medium: M.amber, hard: M.redBright };

type FilterKey = 'subjectId' | 'type' | 'difficulty';

interface Choice {
  value: string;
  label: string;
  /** Identity colour: a subject's own colour, or the difficulty band. */
  dot?: string;
}

/**
 * One chip per filter on the line under the search, as on desktop: a dashed
 * "+ Label" while unset, "Label | value" once set. It opens that filter's sheet.
 */
function FilterChipButton({
  label,
  value,
  onClick,
  onClear,
}: {
  label: string;
  value?: string;
  onClick: () => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  if (value) {
    return (
      // 44px tall row; the visible chip is 34px.
      <span className="flex h-11 flex-shrink-0 items-center">
        <span
          className="inline-flex h-[34px] items-stretch overflow-hidden rounded-md text-[14px]"
          style={{ background: M.card, border: `1px solid ${M.border}` }}
        >
          <button type="button" onClick={onClick} className="inline-flex items-center gap-2 pl-2.5 pr-2">
            <span style={{ color: M.ink3 }}>{label}</span>
            <span aria-hidden className="h-3.5 w-px" style={{ background: M.border }} />
            <span className="max-w-[140px] truncate font-medium" style={{ color: M.ink }}>
              {value}
            </span>
          </button>
          <button
            type="button"
            onClick={onClear}
            aria-label={t('filter_remove', { label })}
            className="inline-flex w-8 items-center justify-center"
            style={{ borderLeft: `1px solid ${M.border}`, color: M.ink3 }}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </span>
      </span>
    );
  }
  return (
    // 44px tap target; the visible chip is 34px.
    <button type="button" onClick={onClick} className="flex h-11 flex-shrink-0 items-center">
      <span
        className="inline-flex h-[34px] items-center gap-1.5 rounded-md px-2.5 text-[14px] font-medium"
        style={{ background: M.card, border: `1px dashed ${M.chevron}`, color: M.ink2 }}
      >
        <Plus className="h-3.5 w-3.5" style={{ color: M.faint }} aria-hidden />
        {label}
      </span>
    </button>
  );
}

interface ChoiceSheetProps {
  onClose: () => void;
  title: string;
  /** The "no filter" row, e.g. "All subjects". */
  allLabel: string;
  choices: Choice[];
  selected?: string;
  onSelect: (value: string | undefined) => void;
  searchable?: boolean;
}

/** One filter's options as radio rows; picking one applies it and closes the sheet. */
function ChoiceSheet({ onClose, title, allLabel, choices, selected, onSelect, searchable }: ChoiceSheetProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const needle = query.trim().toLocaleLowerCase();
  const visible = needle ? choices.filter((c) => c.label.toLocaleLowerCase().includes(needle)) : choices;
  const rows: Array<Choice | { value: undefined; label: string; dot?: undefined }> = needle
    ? visible
    : [{ value: undefined, label: allLabel }, ...visible];

  const pick = (value: string | undefined) => {
    onSelect(value);
    onClose();
  };

  return (
    <BottomSheet open onClose={onClose} maxHeight="80%">
      <h2 className="mb-2 text-[17px] font-bold" style={{ color: M.ink }}>
        {title}
      </h2>
      {searchable && (
        <SearchInput value={query} onChange={setQuery} placeholder={t('search')} className="mb-1" />
      )}
      <div role="radiogroup" aria-label={title}>
        {rows.map((row, index) => {
          const isSelected = row.value === selected;
          return (
            <button
              key={row.value ?? 'all'}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => pick(row.value)}
              className="flex h-12 w-full items-center gap-3 text-left text-[15px]"
              style={{ color: M.ink, borderTop: index === 0 ? 'none' : `1px solid ${M.divider}` }}
            >
              <span
                aria-hidden
                className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full"
                style={{ border: `1.5px solid ${isSelected ? M.ink : M.chevron}` }}
              >
                {isSelected && <span className="h-2.5 w-2.5 rounded-full" style={{ background: M.ink }} />}
              </span>
              {row.dot && <span aria-hidden className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: row.dot }} />}
              <span className="min-w-0 flex-1 truncate" style={{ fontWeight: isSelected ? 600 : 400 }}>
                {row.label}
              </span>
            </button>
          );
        })}
        {rows.length === 0 && (
          <p className="py-3 text-[14px]" style={{ color: M.ink3 }}>
            {t('filter_no_matches')}
          </p>
        )}
      </div>
    </BottomSheet>
  );
}

/** Card list + filter/preview sheets. Same list/subject queries and URL-driven filters as desktop. */
export function QuestionBankMobile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const subjectId = searchParams.get('subjectId') ?? undefined;
  const difficulty = DIFFICULTIES.find((d) => d === searchParams.get('difficulty'));
  const type = searchParams.get('type') ?? undefined;

  // Search is URL-driven too, under `search` like the desktop page, so the two twins share it.
  const { input: searchQuery, setInput: setSearchQuery, query: debouncedSearch } = useSearchParamState('search');

  const { data: subjects = [] } = useSubjectList();

  const canCreate = usePermission(PERMISSIONS.QUESTIONS_CREATE);
  const canEdit = usePermission(PERMISSIONS.QUESTIONS_EDIT);

  const [sheet, setSheet] = useState<FilterKey | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const previewTriggerRef = useRef<HTMLElement | null>(null);

  const openPreview = (id: string, trigger: HTMLElement) => {
    previewTriggerRef.current = trigger;
    trigger.focus();
    setPreviewId(id);
  };

  // Manual "load more" pagination — the bank has no infinite-query hook, so we
  // accumulate pages locally and reset whenever a filter/search changes.
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<BankQuestion[]>([]);
  const [total, setTotal] = useState(0);
  const filterKey = `${subjectId ?? ''}|${difficulty ?? ''}|${type ?? ''}|${debouncedSearch}`;

  useEffect(() => {
    setPage(1);
    setItems([]);
  }, [filterKey]);

  const {
    data: result,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useBankQuestionList({
    subjectId,
    difficulty,
    type,
    search: debouncedSearch || undefined,
    page,
    limit: PAGE_SIZE,
  });

  useEffect(() => {
    if (!result) return;
    setItems((prev) => (page === 1 ? result.data : [...prev, ...result.data]));
    setTotal(result.total);
  }, [result, page]);

  const questions = items;

  const preview = previewId ? questions.find((q) => q.id === previewId) ?? null : null;
  useMobileDockOverlay(Boolean(preview));

  const subjectName = (id: string) => (subjects as Subject[]).find((s) => s.id === id)?.name ?? id;
  const typeLabel = (qType: string) => {
    const meta = QUESTION_TYPE_META[qType];
    return meta?.labelKey ? t(meta.labelKey) : qType;
  };

  const hasFilters = Boolean(subjectId || difficulty || type);
  const isFiltering = hasFilters || Boolean(debouncedSearch);
  // The header counts the whole bank; the line above the list counts the matches.
  const { data: bankCount } = useBankQuestionList({ page: 1, limit: 1 }, { enabled: isFiltering });
  const bankTotal = isFiltering ? bankCount?.total ?? 0 : total;

  // Replace, not push: Back leaves the bank rather than undoing filters one by one.
  const setParam = (key: FilterKey, value: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const clearFilters = () => {
    const params = new URLSearchParams(searchParams);
    ['subjectId', 'difficulty', 'type', 'page'].forEach((k) => params.delete(k));
    setSearchParams(params, { replace: true });
    setSearchQuery('');
  };

  const sheetChoices: Record<FilterKey, { title: string; allLabel: string; choices: Choice[]; selected?: string }> = {
    subjectId: {
      title: t('subject'),
      allLabel: t('all_subjects'),
      choices: (subjects as Subject[]).map((s) => ({ value: s.id, label: s.name, dot: s.color || M.faint })),
      selected: subjectId,
    },
    type: {
      title: t('type'),
      allLabel: t('all_types'),
      choices: QUESTION_TYPE_ORDER.map((qt) => ({ value: qt, label: typeLabel(qt) })),
      selected: type,
    },
    difficulty: {
      title: t('difficulty'),
      allLabel: t('all_levels'),
      choices: DIFFICULTIES.map((d) => ({ value: d, label: t(d), dot: DIFFICULTY_DOT[d] })),
      selected: difficulty,
    },
  };

  // Open the full-page editor. If a subject filter is active, carry it over
  // so the new question form picks the same subject by default (mirrors the
  // desktop page's openCreate).
  const openCreate = () => {
    const qs = subjectId ? `?subjectId=${encodeURIComponent(subjectId)}` : '';
    navigate(`/app/questions/new${qs}`);
  };


  return (
    <MobileScreen>
      <ScreenHeader
        title={t('question_bank')}
        count={formatGroupedNumber(bankTotal)}
        right={
          canCreate && (
            <>
              <HeaderIcon icon={<Upload size={19} />} label={t('import_questions')} onClick={() => setImportOpen(true)} />
              <HeaderPlus onClick={openCreate} label={t('add_question', 'Add question')} />
            </>
          )
        }
      >
        <div className="pt-3">
          <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder={t('qb_search_placeholder')} />
        </div>
        <div className="no-scrollbar -mx-4 flex items-center gap-2 overflow-x-auto px-4 pb-1 pt-1">
          <FilterChipButton
            label={t('subject')}
            value={subjectId ? subjectName(subjectId) : undefined}
            onClick={() => setSheet('subjectId')}
            onClear={() => setParam('subjectId', undefined)}
          />
          <FilterChipButton
            label={t('type')}
            value={type ? typeLabel(type) : undefined}
            onClick={() => setSheet('type')}
            onClear={() => setParam('type', undefined)}
          />
          <FilterChipButton
            label={t('difficulty')}
            value={difficulty ? t(difficulty) : undefined}
            onClick={() => setSheet('difficulty')}
            onClear={() => setParam('difficulty', undefined)}
          />
        </div>
      </ScreenHeader>

      {isLoading && questions.length === 0 ? (
        <MobileSkeleton />
      ) : isError && questions.length === 0 ? (
        <MobileError onRetry={() => refetch()} />
      ) : questions.length === 0 ? (
        <MobileEmpty
          icon={isFiltering ? <Search size={22} /> : <Library size={22} />}
          title={isFiltering ? t('no_results') : t('qb_empty')}
          description={isFiltering ? t('try_adjusting_your_search_or_filters') : undefined}
          action={
            isFiltering ? (
              <button
                type="button"
                onClick={clearFilters}
                className="h-10 rounded-md px-4 text-[14px] font-medium"
                style={{ background: M.card, border: `1px solid ${M.border}`, color: M.ink }}
              >
                {t('clear_filters')}
              </button>
            ) : undefined
          }
        />
      ) : (
        <ScreenBody>
          <p className="mb-3 text-[13px]" style={{ color: M.ink3 }}>
            <span className="font-semibold tabular-nums" style={{ color: M.ink }}>
              {formatGroupedNumber(total)}
            </span>{' '}
            {t('pager_noun_questions')}
            {subjectId && ` · ${subjectName(subjectId)}`}
          </p>
          <div className="overflow-hidden rounded-lg" style={{ background: M.card, border: `1px solid ${M.border}` }}>
            {questions.map((q: BankQuestion, index) => {
              const TypeIcon = (QUESTION_TYPE_META[q.type] ?? DEFAULT_TYPE_META).icon;
              return (
                <div
                  key={q.id}
                  role="button"
                  tabIndex={0}
                  aria-haspopup="dialog"
                  onClick={(event) => openPreview(q.id, event.currentTarget)}
                  onKeyDown={(e) => {
                    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      openPreview(q.id, e.currentTarget);
                    }
                  }}
                  className="flex cursor-pointer flex-col gap-2 px-3.5 py-3"
                  style={{ borderTop: index === 0 ? 'none' : `1px solid ${M.border}` }}
                >
                  <div className="flex items-center gap-2 text-[12.5px]" style={{ color: M.ink3 }}>
                    <TypeIcon className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} aria-hidden />
                    <span className="min-w-0 truncate">{typeLabel(q.type)}</span>
                    {q.difficulty && (
                      <span className="ml-auto flex-shrink-0">
                        <StatusBadge tone={DIFFICULTY_TONE[q.difficulty] ?? 'neutral'} dot={false}>
                          {t(q.difficulty)}
                        </StatusBadge>
                      </span>
                    )}
                  </div>
                  <div className="text-[14.5px]" style={{ color: M.ink }}>
                    <QuestionSummary question={q} />
                  </div>
                  <p className="truncate text-[12.5px]" style={{ color: M.ink3 }}>
                    {subjectName(q.subjectId)} · <span className="tabular-nums">{t('n_points', { count: q.points })}</span>
                  </p>
                </div>
              );
            })}
          </div>
          <LoadMore
            compact
            className="mt-4"
            shown={questions.length}
            total={total}
            step={PAGE_SIZE}
            onLoadMore={() => setPage((p) => p + 1)}
            loading={isFetching && page > 1}
          />
        </ScreenBody>
      )}

      {sheet && (
        <ChoiceSheet
          key={sheet}
          onClose={() => setSheet(null)}
          {...sheetChoices[sheet]}
          onSelect={(value) => setParam(sheet, value)}
          searchable={sheet === 'subjectId' && sheetChoices.subjectId.choices.length > SUBJECT_SEARCH_THRESHOLD}
        />
      )}

      {preview && (
        <QuestionPreviewPanel
          question={preview}
          subjectName={subjectName(preview.subjectId)}
          canEdit={canEdit}
          onEdit={() => navigate(`/app/questions/${preview.id}/edit`)}
          onClose={() => setPreviewId(null)}
          returnFocusRef={previewTriggerRef}
        />
      )}

      <ImportQuestionsModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        defaultSubjectId={subjectId}
      />
    </MobileScreen>
  );
}

export default QuestionBankMobile;
